import { useRequiredContext } from "@redotech/react-util/context";
import type { CustomAnalyticsReportSort } from "@redotech/redo-analytics-common/reports/custom-analytics-report";
import type { CustomAnalyticsReportFilter } from "@redotech/redo-analytics-common/reports/custom-analytics-report-filters";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { useInfiniteQuery } from "@tanstack/react-query";

const CUSTOM_ANALYTICS_REPORT_ROWS_PAGE_SIZE = 25;
const CUSTOM_ANALYTICS_REPORT_ROWS_QUERY_KEY = "customAnalyticsReportRows";

export function getCustomAnalyticsReportRowsQueryKey(
  teamId: string,
  customReportId: string,
  timezone?: string,
  sorts?: readonly CustomAnalyticsReportSort[],
  filters?: readonly CustomAnalyticsReportFilter[],
) {
  return [
    CUSTOM_ANALYTICS_REPORT_ROWS_QUERY_KEY,
    teamId,
    customReportId,
    timezone ?? null,
    sorts ?? [],
    filters ?? [],
  ] as const;
}

export function getCustomAnalyticsReportRowsQueryInvalidationKey(
  teamId: string,
  customReportId?: string,
) {
  return customReportId
    ? ([
        CUSTOM_ANALYTICS_REPORT_ROWS_QUERY_KEY,
        teamId,
        customReportId,
      ] as const)
    : ([CUSTOM_ANALYTICS_REPORT_ROWS_QUERY_KEY, teamId] as const);
}

export function useCustomAnalyticsReportRowsQuery({
  customReportId,
  timezone,
  sorts,
  filters = [],
  enabled = true,
}: {
  customReportId: string;
  timezone?: string;
  sorts?: readonly CustomAnalyticsReportSort[];
  filters?: readonly CustomAnalyticsReportFilter[];
  enabled?: boolean;
}) {
  const team = useRequiredContext(TeamContext);
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);

  return useInfiniteQuery({
    queryKey: getCustomAnalyticsReportRowsQueryKey(
      team._id,
      customReportId,
      timezone,
      sorts,
      filters,
    ),
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      return await rpcClient.getCustomAnalyticsReportRows({
        id: customReportId,
        sorts: sorts ? [...sorts] : undefined,
        filters: [...filters],
        timezone,
        offset: pageParam,
        limit: CUSTOM_ANALYTICS_REPORT_ROWS_PAGE_SIZE,
      });
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextOffset ?? undefined,
    staleTime: 10 * 1000,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    enabled,
  });
}
